import { InputHTMLAttributes, JSX, forwardRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { Control, Controller, FieldErrors, RegisterOptions, UseFormRegister } from "react-hook-form";
import { PatternFormat } from "react-number-format";

import { IconEye, IconEyeSlash, IconMagnifyingGlass } from "@/components/icons";

import { cn } from "@/utils/cn.ts";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: FieldErrors<any>;
  errorMessage?: string;
  type: "text" | "password" | "number" | "search" | "button" | "currency";
  inputSize?: "big" | "medium";
  color?: "black" | "white";
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any>;
  name?: string;
  validation?: RegisterOptions;
  mask?: string;
  icon?: JSX.Element;
  onClick?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      errors,
      errorMessage,
      type,
      color = "black",
      inputSize = "big",
      className,
      register,
      control,
      name,
      validation,
      mask,
      icon,
      onClick,
      disabled,
      ...otherProps
    },
    ref,
  ): JSX.Element => {
    if (register && !name) throw new Error("You must pass name if you are using register or control");
    if (control && !name) throw new Error("You must pass name if you are using control");

    const [passwordShown, setPasswordShown] = useState<boolean>(false);

    const inputClassName = cn(
      "w-full rounded-full outline outline-1 outline-transparent -outline-offset-1 bg-surface-container text-on-surface-variant placeholder-on-surface transition-all hover:bg-surface-container-high focus:bg-surface-container-low max-lg:py-[0.813rem] text-base px-6",
      {
        "!outline-error-1 focus:outline-error-1 placeholder:text-error-1": errors && name && errors[name],
        "input-black bg-surface-container focus:outline-on-primary disabled:bg-surface-container-low":
          color === "black",
        "input-white bg-surface-container disabled:bg-surface-container-low focus:outline-on-primary":
          color === "white",
        "py-[1.125rem] text-lg leading-6": inputSize === "big",
        "text-base py-[.75rem] leading-6": inputSize === "medium",
      },
      className,
    );

    const input: Record<string, JSX.Element> = {
      currency: (
        <Controller
          name={name || ""}
          control={control}
          rules={validation}
          render={({ field: { ref, ...rest } }) => (
            <CurrencyInput
              name={rest.name}
              id={otherProps.id || name}
              className={cn(inputClassName, "disabled:bg-surface-container-low disabled:placeholder:text-[#5E5E5E]", {
                "cursor-pointer placeholder:transition-all group-hover:placeholder:text-white": !disabled,
              })}
              ref={ref}
              decimalsLimit={2}
              disabled={disabled}
              onValueChange={rest.onChange}
              onBlur={rest.onBlur}
              value={rest.value}
              placeholder={otherProps.placeholder}
            />
          )}
        />
      ),
      button: (
        <div className="group relative cursor-pointer" onClick={onClick}>
          <input
            ref={ref}
            type={"text"}
            disabled={disabled}
            readOnly
            {...otherProps}
            {...(register ? register(name ?? "", validation) : null)}
            className={cn(inputClassName, "disabled:bg-surface-container-low disabled:placeholder:text-[#5E5E5E]", {
              "cursor-pointer placeholder:transition-all group-hover:placeholder:text-white": !disabled,
            })}
          />
          {icon && <div className="absolute right-6 top-1/2 -translate-y-1/2">{icon}</div>}
        </div>
      ),
      text: (
        <div className="relative">
          <input
            ref={ref}
            type={type}
            disabled={disabled}
            {...otherProps}
            {...(register ? register(name ?? "", validation) : null)}
            className={inputClassName}
          />
          {icon && <div className="absolute right-6 top-1/2 -translate-y-1/2">{icon}</div>}
        </div>
      ),
      password: (
        <div className={"relative"}>
          <input
            ref={ref}
            type={passwordShown ? "text" : "password"}
            {...(register ? register(name ?? "", validation) : null)}
            className={inputClassName}
            {...otherProps}
            autoComplete={"new-password"}
            disabled={disabled}
          />
          {
            <button
              type={"button"}
              onClick={() => setPasswordShown(!passwordShown)}
              className={"absolute right-6 top-1/2 -translate-y-1/2"}
            >
              {passwordShown ? (
                <IconEye className={"stroke-on-surface size-6"} />
              ) : (
                <IconEyeSlash className={"stroke-on-surface size-6"} />
              )}
            </button>
          }
        </div>
      ),
      number: (
        <Controller
          name={name || ""}
          control={control}
          rules={validation}
          render={({ field: { ref, ...rest } }) => (
            <PatternFormat
              id={otherProps.id || name}
              getInputRef={ref}
              {...rest}
              className={inputClassName}
              format={mask || "####"}
              mask={" "}
              disabled={disabled}
              placeholder={otherProps.placeholder ? otherProps.placeholder : ""}
            />
          )}
        />
      ),
      search: (
        <div className={"relative"}>
          <input
            disabled={disabled}
            ref={ref}
            type={type}
            {...otherProps}
            {...(register ? register(name ?? "", validation) : null)}
            className={cn(inputClassName, "pr-12, placeholder-on-surface-variant")}
          />
          <button type={"submit"} className={"absolute right-6 top-1/2 -translate-y-1/2"} aria-label={"Search"}>
            <IconMagnifyingGlass className={"stroke-on-surface-variant size-6"} />
          </button>
        </div>
      ),
    };

    return (
      <div>
        {input[type]}
        {errorMessage && <p className={"ml-6 w-full pt-0.5 text-start text-sm leading-4 text-red-1"}>{errorMessage}</p>}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
